import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AuthConfigService, AuthInterceptor } from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { EMPTY, Observable, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { EbAuthHttpHeaderService } from '../services/eb-auth-http-header.service'
import { EbAuthStatePersistenceService } from '../services/eb-auth-state-persistence.service'
import { EbAuthService } from '../services/eb-auth.service'

/**
 * Responsible for catching auth errors and providing `Authorization` header for API calls.
 * Uses AuthHttpHeaderService for request manipulation and error handling. Interceptor only hooks into request send/received events.
 */
@Injectable({ providedIn: 'root' })
export class EbAuthInterceptor extends AuthInterceptor {
  constructor(
    protected authHttpHeaderService: EbAuthHttpHeaderService,
    protected authConfigService: AuthConfigService,
    protected ebAuthStatePersistenceService: EbAuthStatePersistenceService,
    protected cookieService: CookieService,
    protected router: Router,
    protected authService: EbAuthService
  ) {
    super(authHttpHeaderService, authConfigService)
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      /orgUsers\/anonymous\b/.test(request?.url) ||
      (/orgUsers\/current\b/.test(request?.url) &&
        !this.accessToken &&
        !this.cookieService.get('asAgent'))
    ) {
      const isSecuredPage = this.authService.isSecuredPage(this.router.routerState.snapshot.url)
        ? '/'
        : undefined
      this.ebAuthStatePersistenceService.internalLogout(false, isSecuredPage)
      return EMPTY
    }
    request = this.authHttpHeaderService.alterRequest(request)
    return next.handle(request).pipe(
      catchError((errResponse: any) => {
        if (
          this.isHttp401(errResponse) &&
          (this.isExpiredToken(errResponse, request) || this.isTokenInvalid(errResponse)) &&
          !this.cookieService.get('asAgent')
        ) {
          this.ebAuthStatePersistenceService.internalLogout(false)
        }
        return throwError(() => errResponse)
      })
    )
  }

  protected isHttp401(response: any): boolean {
    return response instanceof HttpErrorResponse && response.status === 401
  }

  protected isTokenInvalid(response: HttpErrorResponse): boolean {
    return (
      (response.url.includes(this.authConfigService.getTokenEndpoint()) &&
        response.error.error === 'invalid_token') ||
      response.error.errors[0].type === 'InvalidTokenError'
    )
  }

  protected isExpiredToken(response: HttpErrorResponse, request?: HttpRequest<any>): boolean {
    return (
      (response.error?.errors?.[0]?.type === 'InvalidTokenError' ||
        response.error?.errors?.[0]?.type === 'AccessDeniedError' ||
        response.error?.errors?.[0]?.type === 'UnauthorizedError') &&
      this.authHttpHeaderService.shouldCatchError(request)
    )
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }
}
